/* eslint-disable @typescript-eslint/no-use-before-define */
import {
    Action,
    createFeatureSelector,
    createSelector,
    StoreModule,
} from '@ngrx/store';
import { OffroadTrailType } from '@polaris-customerweb/sharedcomponents.core';
import {
    LayerVisibilityAction,
    SetIsShowingAdventures,
    SetIsShowingDealers,
    SetIsShowingDesignatedPavedTrails,
    SetIsShowingDesignatedUnpavedTrails,
    SetIsShowingFood,
    SetIsShowingFuel,
    SetIsShowingGeofences,
    SetIsShowingInProgressRideTrack,
    SetIsShowingLodging,
    SetIsShowingPlannedRides,
    SetIsShowingPublicWaypoints,
    SetIsShowingRideAreas,
    SetIsShowingTrackedRides,
    SetIsShowingTrails,
    SetIsShowingUnknown,
    SetIsShowingUnmaintainedTrails,
    SetIsShowingWaypoints,
    SetLayerVisibility,
    SetMaxRideConditionAgeInHours,
    SetShouldShowCloudCover,
    SetShouldShowCondition,
    SetShouldShowHeatmap,
    SetShouldShowLand,
    SetShouldShowLayerMenu,
    SetShouldShowRadar,
    SetShouldShowRideCondition,
    SetShouldShowSatellite,
    SetShouldShowSnowDepth,
    SetShouldShowTrailDifficulty,
    SetSnapToTrackedRideEnabled,
    SetSnapToTrailEnabled,
    SetTrailFilter,
    ShowTrailTypeChooser,
} from './layer-visibility.actions';

export interface LayerVisibilityState {
    showCloudCover: boolean;
    showDealers: boolean;
    showAdventures: boolean | undefined;
    showMapLayersMenu: boolean;
    showPlannedRides: boolean;
    showFoodPlaces: boolean;
    showFuelPlaces: boolean;
    showLodgingPlaces: boolean;
    showUnknownPlaces: boolean;
    showGeofences: boolean;
    showPublicWaypoints: boolean;
    showRadar: boolean;
    showLand: boolean;
    showRideAreas: boolean;
    showSatellite: boolean;
    showSnowDepth: boolean;
    showTrackedRides: boolean;
    showInProgressRide: boolean;
    showWaypoints: boolean;
    showRideCondition: boolean | undefined;
    showCondition: boolean | undefined;
    showOhmTrails: boolean;
    showAtvTrails: boolean;
    showRov50Trails: boolean;
    showRovTrails: boolean;
    showAnyTrails: boolean;
    showDesignatedUnpavedTrails: boolean;
    showDesignatedPavedTrails: boolean;
    showUnmaintainedTrails: boolean | undefined;
    /** @deprecated use showTrails, orv and snow trail values are now shared */
    showOrvTrails?: boolean | undefined;
    /** @deprecated use showTrails, orv and snow trail values are now shared */
    showSnoTrails?: boolean | undefined;
    showTrails: boolean | undefined;
    snapToTrackedRideEnabled: boolean;
    snapToTrailEnabled: boolean;
    showTrailTypeChooser: boolean;
    showTrailDifficulty?: boolean | undefined;
    showHeatMap: boolean | undefined;
    trailFilter: OffroadTrailType | undefined;
    maxRideConditionAgeInHours: number;
}

const initialLayerVisibility: LayerVisibilityState = {
    maxRideConditionAgeInHours: 24,
    showAdventures: true,
    showAnyTrails: true,
    showAtvTrails: true,
    showCloudCover: false,
    showCondition: true,
    showDealers: true,
    showDesignatedPavedTrails: true,
    showDesignatedUnpavedTrails: true,
    showFoodPlaces: false,
    showFuelPlaces: false,
    showGeofences: true,
    showHeatMap: true,
    showInProgressRide: true,
    showLand: false,
    showLodgingPlaces: false,
    showMapLayersMenu: false,
    showOhmTrails: true,
    showPlannedRides: true,
    showPublicWaypoints: true,
    showRadar: false,
    showRideAreas: true,
    showRideCondition: false,
    showRov50Trails: true,
    showRovTrails: true,
    showSatellite: false,
    showSnowDepth: false,
    showTrackedRides: true,
    showTrailDifficulty: true,
    showTrailTypeChooser: false,
    showTrails: true,
    showUnknownPlaces: false,
    showUnmaintainedTrails: true,
    showWaypoints: true,
    snapToTrackedRideEnabled: true,
    snapToTrailEnabled: true,
    trailFilter: undefined,
};

export const selectLayerVisibilityState =
    createFeatureSelector<LayerVisibilityState>('layerVisibilityState');

export const selectIsShowingMapLayersMenu = createSelector(
    selectLayerVisibilityState,
    (state) => state.showMapLayersMenu
);
export const selectIsShowingCloudCover = createSelector(
    selectLayerVisibilityState,
    (state) => state.showCloudCover
);
export const selectIsShowingDealers = createSelector(
    selectLayerVisibilityState,
    (state) => state.showDealers
);
export const selectIsShowingAdventures = createSelector(
    selectLayerVisibilityState,
    (state) => state.showAdventures
);
export const selectIsShowingPlannedRides = createSelector(
    selectLayerVisibilityState,
    (state) => state.showPlannedRides
);

export const selectIsShowingFood = createSelector(
    selectLayerVisibilityState,
    (state) => state.showFoodPlaces
);
export const selectIsShowingFuel = createSelector(
    selectLayerVisibilityState,
    (state) => state.showFuelPlaces
);
export const selectIsShowingLodging = createSelector(
    selectLayerVisibilityState,
    (state) => state.showLodgingPlaces
);
export const selectIsShowingUnknown = createSelector(
    selectLayerVisibilityState,
    (state) => state.showUnknownPlaces
);
export const selectIsShowingGeofences = createSelector(
    selectLayerVisibilityState,
    (state) => state.showGeofences
);
export const selectIsShowingPublicWaypoints = createSelector(
    selectLayerVisibilityState,
    (state) => state.showPublicWaypoints
);
export const selectIsShowingRadar = createSelector(
    selectLayerVisibilityState,
    (state) => state.showRadar
);
export const selectIsShowingLand = createSelector(
    selectLayerVisibilityState,
    (state) => state.showLand
);
export const selectIsShowingRideAreas = createSelector(
    selectLayerVisibilityState,
    (state) => state.showRideAreas
);
export const selectIsShowingSnowDepth = createSelector(
    selectLayerVisibilityState,
    (state) => state.showSnowDepth
);
export const selectIsShowingSatellite = createSelector(
    selectLayerVisibilityState,
    (state) => state.showSatellite
);
export const selectIsShowingTrackedRides = createSelector(
    selectLayerVisibilityState,
    (state) => state.showTrackedRides
);

export const selectIsShowingInProgressRide = createSelector(
    selectLayerVisibilityState,
    (state) => state.showInProgressRide
);
export const selectIsShowingTrailDifficulty = createSelector(
    selectLayerVisibilityState,
    (state) => state.showTrailDifficulty
);
export const selectIsShowingHeatmap = createSelector(
    selectLayerVisibilityState,
    (state) => state.showHeatMap
);
export const selectIsShowingWaypoints = createSelector(
    selectLayerVisibilityState,
    (state) => state.showWaypoints
);
export const selectIsShowingRideConditions = createSelector(
    selectLayerVisibilityState,
    (state) => state.showRideCondition ?? false
);

export const selectIsShowingConditions = createSelector(
    selectLayerVisibilityState,
    (state) => state.showCondition ?? false
);

export const selectMaxRideConditionsAgeInHours = createSelector(
    selectLayerVisibilityState,
    (state) => {
        return state.maxRideConditionAgeInHours;
    }
);

export const selectLayerVisibility = createSelector(
    selectLayerVisibilityState,
    (state) => state
);

export const selectIsShowingOhmTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showOhmTrails
);

export const selectIsShowingAtvTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showAtvTrails
);
export const selectIsShowingRov50Trails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showRov50Trails
);
export const selectIsShowingRovTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showRovTrails
);
export const selectIsShowingAnyTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showAnyTrails
);
export const selectIsShowingDesignatedUnpavedTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showDesignatedUnpavedTrails
);
export const selectIsShowingDesignatedPavedTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showDesignatedPavedTrails
);
export const selectIsShowingUnmaintainedTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showUnmaintainedTrails
);

export const selectIsShowingTrails = createSelector(
    selectLayerVisibilityState,
    (state) => state.showTrails
);

export const selectSnapToTrackedRideEnabled = createSelector(
    selectLayerVisibilityState,
    (state) => state.snapToTrackedRideEnabled
);

export const selectSnapToTrailEnabled = createSelector(
    selectLayerVisibilityState,
    (state) => state.snapToTrailEnabled
);

export const selectShowTrailTypeChooser = createSelector(
    selectLayerVisibilityState,
    (state) => state.showTrailTypeChooser
);

export const selectTrailFilter = createSelector(
    selectLayerVisibilityState,
    (state) => state.trailFilter
);

export const layerVisibilityStoreModule = StoreModule.forFeature(
    'layerVisibilityState',
    layerVisibilityReducer
);

export function layerVisibilityReducer(
    state = initialLayerVisibility,
    action: Action
): LayerVisibilityState {
    let layerVisibility: LayerVisibilityState | undefined;

    switch (action.type) {
        case LayerVisibilityAction.setLayerVisibility:
            // if a user had the preferences saved before then
            // ride condition is null, we have to set it to default in this case
            layerVisibility =
                (action as SetLayerVisibility | undefined)?.layerVisibility ||
                initialLayerVisibility;
            layerVisibility.maxRideConditionAgeInHours =
                layerVisibility.maxRideConditionAgeInHours ||
                initialLayerVisibility.maxRideConditionAgeInHours;
            // default trail difficulty if non-existent
            layerVisibility.showTrailDifficulty =
                layerVisibility.showTrailDifficulty ??
                initialLayerVisibility.showTrailDifficulty;
            // default adventures if non-existent
            layerVisibility.showAdventures =
                layerVisibility.showAdventures ??
                initialLayerVisibility.showAdventures;
            // if non-existent:
            // default showTrails to old values
            // if old values non-existent, use default
            layerVisibility.showTrails =
                layerVisibility.showTrails ??
                (layerVisibility.showOrvTrails ||
                    layerVisibility.showSnoTrails) ??
                initialLayerVisibility.showTrails;
            //default showHeatMap if non-existent
            layerVisibility.showHeatMap =
                layerVisibility.showHeatMap ??
                initialLayerVisibility.showHeatMap;
            //default showUnmaintainedTrails if non-existent
            layerVisibility.showUnmaintainedTrails =
                layerVisibility.showUnmaintainedTrails ??
                initialLayerVisibility.showUnmaintainedTrails;
            // default showCondition to old ride condition or true if non-existent
            layerVisibility.showCondition =
                layerVisibility.showCondition ??
                layerVisibility.showRideCondition ??
                initialLayerVisibility.showCondition;

            return layerVisibility;
        case LayerVisibilityAction.setShouldShowLayerMenu:
            return {
                ...state,
                showMapLayersMenu: (action as SetShouldShowLayerMenu)
                    .shouldShow,
            };
        case LayerVisibilityAction.setShouldShowSatellite:
            return {
                ...state,
                showSatellite: (action as SetShouldShowSatellite).shouldShow,
            };
        case LayerVisibilityAction.toggleShouldShowMapLayersMenu:
            return { ...state, showMapLayersMenu: !state.showMapLayersMenu };
        case LayerVisibilityAction.setShouldShowRadar:
            return {
                ...state,
                showRadar: (action as SetShouldShowRadar).shouldShow,
            };
        case LayerVisibilityAction.setShouldShowLand:
            return {
                ...state,
                showLand: (action as SetShouldShowLand).shouldShow,
            };
        case LayerVisibilityAction.setShouldShowCloudCover:
            return {
                ...state,
                showCloudCover: (action as SetShouldShowCloudCover).shouldShow,
            };
        case LayerVisibilityAction.setShouldShowSnowDepth:
            return {
                ...state,
                showSnowDepth: (action as SetShouldShowSnowDepth).shouldShow,
            };
        case LayerVisibilityAction.setShouldShowRideCondition:
            return {
                ...state,
                showRideCondition: (action as SetShouldShowRideCondition)
                    .shouldShow,
            };
        case LayerVisibilityAction.setShouldShowCondition:
            return {
                ...state,
                showCondition: (action as SetShouldShowCondition).shouldShow,
            };
        case LayerVisibilityAction.setShouldShowTrailDifficulty:
            return {
                ...state,
                showTrailDifficulty: (action as SetShouldShowTrailDifficulty)
                    .shouldShow,
            };
        case LayerVisibilityAction.setShouldShowHeatmap:
            return {
                ...state,
                showHeatMap: (action as SetShouldShowHeatmap).shouldShow,
            };
        case LayerVisibilityAction.setIsShowingDealers:
            return {
                ...state,
                showDealers: (action as SetIsShowingDealers).isShowing,
            };
        case LayerVisibilityAction.setIsShowingAdventures:
            return {
                ...state,
                showAdventures: (action as SetIsShowingAdventures).isShowing,
            };
        case LayerVisibilityAction.setMaxRideConditionAgeInHours:
            return {
                ...state,
                maxRideConditionAgeInHours: (
                    action as SetMaxRideConditionAgeInHours
                ).maxRideConditionAgeInHours,
            };
        case LayerVisibilityAction.setIsShowingPlannedRides:
            return {
                ...state,
                showPlannedRides: (action as SetIsShowingPlannedRides)
                    .isShowing,
            };
        case LayerVisibilityAction.setIsShowingFood:
            return {
                ...state,
                showFoodPlaces: (action as SetIsShowingFood).isShowing,
            };
        case LayerVisibilityAction.setIsShowingFuel:
            return {
                ...state,
                showFuelPlaces: (action as SetIsShowingFuel).isShowing,
            };
        case LayerVisibilityAction.setIsShowingLodging:
            return {
                ...state,
                showLodgingPlaces: (action as SetIsShowingLodging).isShowing,
            };
        case LayerVisibilityAction.setIsShowingUnknown:
            return {
                ...state,
                showUnknownPlaces: (action as SetIsShowingUnknown).isShowing,
            };
        case LayerVisibilityAction.setIsShowingGeofences:
            return {
                ...state,
                showGeofences: (action as SetIsShowingGeofences).isShowing,
            };
        case LayerVisibilityAction.setIsShowingPublicWaypoints:
            return {
                ...state,
                showPublicWaypoints: (action as SetIsShowingPublicWaypoints)
                    .isShowing,
            };
        case LayerVisibilityAction.setIsShowingRideAreas:
            return {
                ...state,
                showRideAreas: (action as SetIsShowingRideAreas).isShowing,
            };
        case LayerVisibilityAction.setIsShowingTrackedRides:
            return {
                ...state,
                showTrackedRides: (action as SetIsShowingTrackedRides)
                    .isShowing,
            };
        case LayerVisibilityAction.setIsShowingInProgressRideTrack:
            return {
                ...state,
                showInProgressRide: (action as SetIsShowingInProgressRideTrack)
                    .isShowing,
            };
        case LayerVisibilityAction.setIsShowingWaypoints:
            return {
                ...state,
                showWaypoints: (action as SetIsShowingWaypoints).isShowing,
            };
        case LayerVisibilityAction.setIsShowingDesignatedUnpavedTrails:
            return {
                ...state,
                showDesignatedUnpavedTrails: (
                    action as SetIsShowingDesignatedUnpavedTrails
                ).isShowing,
            };
        case LayerVisibilityAction.setIsShowingDesignatedPavedTrails:
            return {
                ...state,
                showDesignatedPavedTrails: (
                    action as SetIsShowingDesignatedPavedTrails
                ).isShowing,
            };
        case LayerVisibilityAction.setIsShowingUnmaintainedTrails:
            return {
                ...state,
                showUnmaintainedTrails: (
                    action as SetIsShowingUnmaintainedTrails
                ).isShowing,
            };
        case LayerVisibilityAction.setIsShowingTrails:
            return {
                ...state,
                showTrails: (action as SetIsShowingTrails).isShowing,
            };
        case LayerVisibilityAction.setSnapToTrackedRideEnabled:
            return {
                ...state,
                snapToTrackedRideEnabled: (
                    action as SetSnapToTrackedRideEnabled
                ).isShowing,
            };
        case LayerVisibilityAction.setSnapToTrailEnabled:
            return {
                ...state,
                snapToTrailEnabled: (action as SetSnapToTrailEnabled).isShowing,
            };
        case LayerVisibilityAction.showTrailTypeChooser:
            return {
                ...state,
                showTrailTypeChooser: (action as ShowTrailTypeChooser)
                    .isShowing,
            };
        case LayerVisibilityAction.setTrailFilter:
            return {
                ...state,
                trailFilter: (action as SetTrailFilter).trailFilter,
            };

        default:
            return state;
    }
}
